import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as loginApi, setupTenant, checkSetup, requestMagicLink, verifyMagicLink, trustDevice } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, RefreshCw, ArrowLeft, Mail, Shield, KeyRound, Fingerprint } from 'lucide-react';
import api from '../lib/api';

const TRUSTED_DEVICE_KEY = 'trusted_device_token';

/* ---- WebAuthn base64url helpers ---- */
function _b64ToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function _bufferToB64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, needsSetup, isAuthenticated, sessionExpiredReason, clearSessionExpired } = useAuth();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [trustBrowser, setTrustBrowser] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'magic-link-sent'
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');

  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // Forgot password form
  const [forgotEmail, setForgotEmail] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  /**
   * Passkey login flow:
   * 1. Request authentication options (challenge) from server
   * 2. Call navigator.credentials.get() — triggers Touch ID / Face ID / security key
   * 3. Send the assertion back to server for verification
   * 4. Server returns JWT if successful
   */
  const handlePasskeyLogin = async () => {
    if (!email) { toast.error('Enter your email first'); return; }
    if (!window.PublicKeyCredential) { toast.error('Passkeys not supported in this browser'); return; }

    setPasskeyLoading(true);
    try {
      // Step 1: Get authentication options
      const { data: optData } = await api.post('/passkeys/login-options', { email });
      const options = JSON.parse(optData.options);

      options.challenge = _b64ToBuffer(options.challenge);
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({
          ...c, id: _b64ToBuffer(c.id),
        }));
      }

      // Step 2: Get assertion (triggers biometric prompt)
      const assertion = await navigator.credentials.get({ publicKey: options });

      // Step 3: Send to server
      const credentialJSON = {
        id: assertion.id,
        rawId: _bufferToB64(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: _bufferToB64(assertion.response.authenticatorData),
          clientDataJSON: _bufferToB64(assertion.response.clientDataJSON),
          signature: _bufferToB64(assertion.response.signature),
          userHandle: assertion.response.userHandle ? _bufferToB64(assertion.response.userHandle) : null,
        },
      };

      const { data } = await api.post('/passkeys/login-complete', {
        credential: credentialJSON,
        email,
      });

      login(data.token, data.user);

      if (data.requires_password_reset) {
        toast.info('Please change your password');
        navigate('/profile?reset=true');
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Passkey authentication cancelled');
      } else if (err.name === 'SecurityError') {
        toast.error('Passkey not allowed on this domain — ensure you\'re using HTTPS');
      } else if (err.response?.status === 400 && err.response?.data?.detail?.includes('No passkeys')) {
        toast.error('No passkeys registered for this account');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Passkey authentication failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Setup form
  const [tenantName, setTenantName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // Track if we're handling login redirect manually (e.g., password reset)
  const handlingRedirect = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !handlingRedirect.current) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setIsSetup(needsSetup);
  }, [needsSetup]);

  // Handle magic link token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token && searchParams.get('reset')) {
      handleMagicLinkVerify(token);
    }
  }, [searchParams]);

  // Load reCAPTCHA settings from PUBLIC endpoint (no auth needed)
  useEffect(() => {
    const loadRecaptcha = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/auth/public-settings`);
        const settings = await res.json();
        if (settings?.recaptcha_enabled && settings?.recaptcha_site_key) {
          setRecaptchaSiteKey(settings.recaptcha_site_key);
          const script = document.createElement('script');
          script.src = `https://www.google.com/recaptcha/api.js?render=${settings.recaptcha_site_key}`;
          script.onload = () => setRecaptchaReady(true);
          document.head.appendChild(script);
        }
      } catch (e) {
        // Settings not available — ignore
      }
    };
    loadRecaptcha();
  }, []);

  const getRecaptchaToken = useCallback(async () => {
    if (!recaptchaReady || !recaptchaSiteKey || !window.grecaptcha) return null;
    try {
      return await window.grecaptcha.execute(recaptchaSiteKey, { action: 'login' });
    } catch { return null; }
  }, [recaptchaReady, recaptchaSiteKey]);

  const handleMagicLinkVerify = async (token) => {
    setLoading(true);
    try {
      const res = await verifyMagicLink(token);
      const data = res.data;
      handlingRedirect.current = true;
      login(data.token, data.user);
      toast.success('Password reset successful. Please set a new password.');
      navigate('/profile?reset=true');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken();
      const trustedToken = localStorage.getItem(TRUSTED_DEVICE_KEY);

      const response = await loginApi({ 
        email, 
        password, 
        mfa_code: requiresMFA ? mfaCode : null,
        trusted_device_token: trustedToken || null,
        recaptcha_token: recaptchaToken
      });
      
      const data = response.data;
      
      if (data.requires_mfa && !data.token) {
        setRequiresMFA(true);
        setPendingLogin(data);
        toast.info('Enter your authentication code');
        setLoading(false);
        return;
      }
      
      // Store trusted device token if returned
      if (data.trusted_device_token) {
        localStorage.setItem(TRUSTED_DEVICE_KEY, data.trusted_device_token);
      }
      
      // Set redirect flag before login to prevent useEffect redirect
      if (data.requires_password_reset) {
        handlingRedirect.current = true;
      }
      
      login(data.token, data.user);
      
      // If MFA was verified and user wants to trust browser, create trust token
      if (requiresMFA && trustBrowser && data.token) {
        try {
          const trustRes = await trustDevice();
          localStorage.setItem(TRUSTED_DEVICE_KEY, trustRes.data.trusted_device_token);
        } catch (e) {
          // Non-critical
        }
      }
      
      if (data.requires_password_reset) {
        toast.info('Please change your password');
        navigate('/profile?reset=true');
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(requiresMFA ? 'Invalid authentication code' : 'Invalid email or password');
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Access denied');
      } else {
        toast.error(err.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestMagicLink({ email: forgotEmail, security_code: securityCode, frontend_url: window.location.origin });
      setView('magic-link-sent');
      toast.success('If the account exists, a reset link has been sent.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await setupTenant(
        { name: tenantName },
        { name: adminName, email: adminEmail, password: adminPassword }
      );
      login(response.data.token, response.data.user);
      toast.success('System setup complete!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  // ===== FORGOT PASSWORD VIEW =====
  if (view === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4" data-testid="forgot-password-page">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-violet-600" />
            </div>
            <CardTitle className="text-xl">Forgot Password</CardTitle>
            <CardDescription>Enter your email and security code to receive a reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input 
                  id="forgot-email" type="email" required value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="input-forgot-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="security-code">Security Code</Label>
                <Input 
                  id="security-code" type="password" required value={securityCode}
                  onChange={e => setSecurityCode(e.target.value)}
                  placeholder="Enter your company security code"
                  data-testid="input-security-code"
                />
                <p className="text-xs text-muted-foreground">This code is provided by your administrator</p>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="btn-request-magic-link">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Reset Link
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => setView('login')} data-testid="btn-back-to-login">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== MAGIC LINK SENT VIEW =====
  if (view === 'magic-link-sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4" data-testid="magic-link-sent-page">
        <Card className="w-full max-w-md shadow-lg border-0 text-center">
          <CardHeader className="pb-2">
            <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Check Your Email</CardTitle>
            <CardDescription>If an account with that email exists, we've sent a password reset link. It expires in 15 minutes.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full mt-4" onClick={() => { setView('login'); setForgotEmail(''); setSecurityCode(''); }} data-testid="btn-back-to-login-after-sent">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== LOGIN VIEW (with MFA step) =====
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4" data-testid="login-page">
      <Card className="w-full max-w-md shadow-lg border-0">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold tracking-tight">
            {isSetup ? 'System Setup' : 'Welcome Back'}
          </CardTitle>
          <CardDescription>
            {isSetup ? 'Set up your system to get started' : requiresMFA ? 'Enter your authentication code' : 'Sign in to your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Session expired message */}
          {sessionExpiredReason && (
            <div data-testid="session-expired-banner" className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span>{sessionExpiredReason}</span>
            </div>
          )}
          {isSetup ? (
            <form onSubmit={handleSetup} className="space-y-4" data-testid="setup-form">
              <div className="space-y-2">
                <Label htmlFor="tenant">Company Name</Label>
                <Input id="tenant" required value={tenantName} onChange={(e) => setTenantName(e.target.value)} placeholder="Your Company" data-testid="input-tenant-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-name">Admin Name</Label>
                <Input id="admin-name" required value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Admin Name" data-testid="input-admin-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Admin Email</Label>
                <Input id="admin-email" type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@company.com" data-testid="input-admin-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Admin Password</Label>
                <Input id="admin-password" type="password" required value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Strong password" data-testid="input-admin-password" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="btn-setup">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Setup System
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              {!requiresMFA ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="input-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input 
                        id="password" type={showPassword ? 'text' : 'password'} required 
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your password" data-testid="input-password"
                      />
                      <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Two-Factor Authentication</span>
                    </div>
                    <p className="text-xs text-blue-600">Open your authenticator app and enter the 6-digit code</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code">Authentication Code</Label>
                    <Input 
                      id="mfa-code" required autoFocus value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000" className="text-center text-2xl tracking-[0.5em] font-mono"
                      maxLength={6} data-testid="input-mfa-code"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="trust-browser" checked={trustBrowser} onCheckedChange={setTrustBrowser} data-testid="checkbox-trust-browser" />
                    <label htmlFor="trust-browser" className="text-sm text-muted-foreground cursor-pointer">
                      Trust this browser for 90 days
                    </label>
                  </div>
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={loading} data-testid="btn-login">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {requiresMFA ? 'Verify' : 'Sign In'}
              </Button>
              
              {requiresMFA && (
                <Button type="button" variant="ghost" className="w-full text-sm" onClick={() => { setRequiresMFA(false); setMfaCode(''); setPendingLogin(null); }}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              )}
              
              {!requiresMFA && (
                <>
                  {/* Passkey login — only show when email is filled and browser supports it */}
                  {window.PublicKeyCredential && (
                    <>
                      <div className="relative my-1">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">or</span></div>
                      </div>
                      <Button
                        type="button" variant="outline" className="w-full"
                        onClick={handlePasskeyLogin}
                        disabled={passkeyLoading || !email}
                        data-testid="btn-passkey-login"
                      >
                        {passkeyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                        Sign in with Passkey
                      </Button>
                    </>
                  )}
                  <div className="text-center pt-1">
                    <button type="button" onClick={() => setView('forgot')} className="text-sm text-primary hover:underline" data-testid="link-forgot-password">
                      Forgot password?
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
